import CircleExclammationIcon from '@src/svgs/CircleExclammationIcon';
import CloseIcon from '@src/svgs/CloseIcon';
import React from 'react';
import styled, { keyframes } from 'styled-components';

interface RefreshNotificationProps {
  isVisible: boolean;
  onRefresh: () => void;
  onDismiss: () => void;
}

const RefreshNotification: React.FC<RefreshNotificationProps> = ({
  isVisible,
  onRefresh,
  onDismiss,
}) => {
  if (!isVisible) return null;

  return (
    <NotificationContainer>
      <NotificationContent>
        <TitleContent>
          <CircleExclammationIcon strokeColor="#45d483" />
          <Title>Software Update</Title>
        </TitleContent>
        <Description>
          A new version of Arbor is available.{'\n'}To get the latest version
          hit <strong>Save and Refresh</strong>.
        </Description>
        <ButtonContainer>
          <DismissButton onClick={onDismiss}>Later</DismissButton>
          <BaseButton onClick={onRefresh}>Save and Refresh</BaseButton>
        </ButtonContainer>
        <CloseIconContainer onClick={onDismiss}>
          <CloseIcon />
        </CloseIconContainer>
      </NotificationContent>
    </NotificationContainer>
  );
};

export default RefreshNotification;

// Animations
const slideUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

// Styled components
const NotificationContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 10000002;
  animation: ${slideUp} 0.3s ease-out;
  max-width: 460px;

  @media (max-width: 480px) {
    left: 20px;
    right: 20px;
    max-width: none;
  }
`;

const NotificationContent = styled.div`
  background: #03041a;
  border-radius: 16px;
  border: 1px solid #484848;
  padding: 32px 24px;
  box-shadow: 8px 8px 8px 0 rgba(0, 0, 0, 0.24);
  display: flex;
  flex-direction: column;
  gap: 24px;
  position: relative;
`;

const TitleContent = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
`;

const Title = styled.h3`
  margin: 0;
  color: #45d483;
  text-align: center;
  font-family: Inter;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 1.92px;
  text-transform: uppercase;
`;

const Description = styled.p`
  margin: 0;
  color: #f3e9d7;
  text-align: center;
  font-size: 16px;
  font-weight: 400;
  line-height: normal;
  white-space: pre-line;
  strong {
    font-weight: 700;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const BaseButton = styled.button`
  display: flex;
  height: 48px;
  padding: 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
  border-radius: 8px;
  background: #17c964;
  color: #03041a;
  border: none;
  outline: none;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.2) saturate(1.2);
  }

  &:active {
    transform: translateY(0);
  }
`;

const DismissButton = styled(BaseButton)`
  color: #17c964;
  border: 1px solid #17c964;
  background: transparent;
`;

const CloseIconContainer = styled.div`
  position: absolute;
  top: 24px;
  right: 24px;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    filter: brightness(1.2) saturate(1.2);
  }
`;
